import { forwardRef, useId } from 'react';
import styled from 'styled-components';
import { space } from '../../../../styles/onboardingUiTokens';
import { DEFAULT_FORM_HINT_PLACEMENT, FormFieldError, type FormHintPlacement } from './formFieldLayout';
import { GroupLabelLegend } from './Label';
import { TextInput } from './TextInput';

const Fieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: none;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${space.sm};
  width: 100%;
  flex-wrap: wrap;
`;

const Cell = styled.div<{ $flex: number }>`
  flex: ${({ $flex }) => $flex};
  min-width: min(100%, 5rem);
`;

export type PhoneNumberInputProps = {
  nationalNumber?: string;
  countryCode?: string;
  setNationalNumber: (value: string) => void;
  setCountryCode: (value: string) => void;
  phoneError?: string;
  onBlurPhone?: () => void;
  /** Root id for the fieldset; child inputs get derived ids. */
  id?: string;
  /** Fieldset legend above both inputs; uses primary label styling. */
  groupLegend?: string;
  /** Required / optional markers on the group legend (not on per-field labels). */
  groupLabelRequired?: boolean;
  groupLabelOptional?: boolean;
  groupOptionalLabel?: string;
  countryLabel?: string;
  nationalLabel?: string;
  countryHint?: string;
  nationalHint?: string;
  /** Applied to both country and national fields. Default: below. */
  hintPlacement?: FormHintPlacement;
  /** Disables both country and national inputs. */
  disabled?: boolean;
};

export const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  function PhoneNumberInput(props, ref) {
    const {
      nationalNumber = '',
      countryCode = '',
      setNationalNumber,
      setCountryCode,
      phoneError,
      onBlurPhone,
      id: idProp,
      groupLegend,
      groupLabelRequired = false,
      groupLabelOptional = false,
      groupOptionalLabel,
      countryLabel,
      nationalLabel,
      countryHint,
      nationalHint,
      hintPlacement = DEFAULT_FORM_HINT_PLACEMENT,
      disabled = false,
    } = props;

    const reactId = useId();
    const rootId = idProp ?? `${reactId}-phone`;
    const countryId = `${rootId}-country`;
    const nationalId = `${rootId}-national`;
    const phoneErrorId = `${rootId}-error`;
    const hasPhoneError = Boolean(phoneError?.trim());

    const handleNationalChange = (value: string) => {
      setNationalNumber(value.replace(/\D/g, ''));
    };

    const handleCountryChange = (value: string) => {
      let next = `+${value.replace(/[^0-9]/g, '')}`;
      if (next.length > 5) {
        next = next.slice(0, 5);
      }
      setCountryCode(next);
    };

    const normalizeNationalOnBlur = () => {
      if (nationalNumber.startsWith('0')) {
        setNationalNumber(nationalNumber.slice(1));
      }
      onBlurPhone?.();
    };

    return (
      <Fieldset id={rootId}>
        {groupLegend != null && groupLegend !== '' ? (
          <GroupLabelLegend
            required={groupLabelRequired}
            optional={groupLabelOptional}
            optionalText={groupOptionalLabel}
          >
            {groupLegend}
          </GroupLabelLegend>
        ) : null}
        <Row>
          <Cell $flex={1}>
            <TextInput
              id={countryId}
              label={countryLabel}
              labelVariant="compact"
              hint={countryHint}
              hintPlacement={hintPlacement}
              value={countryCode}
              onChange={handleCountryChange}
              placeholder="+49"
              type="tel"
              inputMode="tel"
              autoComplete="tel-country-code"
              disabled={disabled}
              ariaInvalid={hasPhoneError}
              aria-describedby={hasPhoneError ? phoneErrorId : undefined}
            />
          </Cell>
          <Cell $flex={4}>
            <TextInput
              ref={ref}
              id={nationalId}
              label={nationalLabel}
              labelVariant="compact"
              hint={nationalHint}
              hintPlacement={hintPlacement}
              value={nationalNumber}
              onChange={handleNationalChange}
              placeholder="Telefonnummer (Mobil)"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              onBlur={normalizeNationalOnBlur}
              disabled={disabled}
              ariaInvalid={hasPhoneError}
              aria-describedby={hasPhoneError ? phoneErrorId : undefined}
            />
          </Cell>
        </Row>
        <FormFieldError id={phoneErrorId} message={phoneError} />
      </Fieldset>
    );
  },
);

PhoneNumberInput.displayName = 'PhoneNumberInput';
